import React, { FunctionComponent, useEffect, useState } from "react";
import styled from "styled-components";
import CustomButton from "../../atoms/customButton";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SupportIcon from "@mui/icons-material/Support";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CurrencyBitcoinIcon from "@mui/icons-material/CurrencyBitcoin";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import HubIcon from "@mui/icons-material/Hub";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PaymentsIcon from "@mui/icons-material/Payments";
import PoolIcon from "@mui/icons-material/Pool";
import HearingIcon from "@mui/icons-material/Hearing";
import EmailIcon from "@mui/icons-material/Email";
import { Link, useNavigate } from "react-router-dom";

const Title = styled.h2`
  font-family: Montserrat, sans-serif;
  font-weight: 400;
  line-height: 1.9em;
  color: white;
  padding-bottom: 2px;
  margin-bottom: 2px;
  font-size: 28px;
`;

const SubTitle = styled.h2`
  font-family: Montserrat, sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 1.9em;
  color: white;
  padding-bottom: 2px;
  margin-bottom: 2px;
`;

const SubDescription = styled.h2`
  font-family: Montserrat, sans-serif;
  font-weight: 400;
  font-size: 14px;
  font-style: italic;
  line-height: 1em;
  color: white;
  padding-bottom: 2px;
  margin-bottom: 2px;
`;

const BoldSubTitle = styled.h2`
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.9em;
  color: white;
  padding-bottom: 0px;
  margin-bottom: 2px;
`;

const Description = styled.h4`
  font-family: Montserrat, sans-serif;
  font-weight: 400;
  line-height: 1.9em;
  color: #b3b3b3;
  margin: 5px 0px;
  font-size: 14px;
`;

const BoldLinks = styled.a`
  font-family: Montserrat, sans-serif;
  text-decoration: underline;
  color: #ffffff;
  font-weight: 600;
  line-height: 1.9em;

  &:hover {
    color: #fcaeea;
  }
`;

const SelfBoldLink = styled.div`
  font-family: Montserrat, sans-serif;
  text-decoration: underline;
  color: #ffffff;
  font-weight: 600;
  line-height: 1.9em;
  cursor: pointer;
  &:hover {
    color: #fcaeea;
  }
`;

const StackButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: 32px;
`;

const CustomDotpoint = styled.li`
  font-family: Montserrat, sans-serif;
  font-weight: 400;
  line-height: 1.9em;
  margin: 5px 0px;
  font-size: 14px;
  color: #b3b3b3;
`;

const TextIcon = styled.div`
  display: flex;
  flex-direction: row;
`;

const CustomIcon = styled.div`
  display: flex;
  flex-direction: column;
  color: #fcaeea;
  justify-content: center;
  margin-left: 4px;
  margin-top: 6px;
`;

const ExperienceDetails: FunctionComponent = () => {
  let navigate = useNavigate();

  const onClickFirstRescue = () => {
    navigate("../showcase/firstrescue", { replace: true });
    navigate(0);
  };

  return (
    <>
      <Title>About</Title>
      <Description>
        I am a senior full stack TypeScript engineer and consultant with 8 years
        of experience delivering enterprise web, mobile, cloud, and AI-powered
        solutions across banking, fintech, government, insurance, and media
        industries. Specialising in TypeScript, React, React Native, Next.js,
        Node.js, and cloud-native technologies, I have led the design and
        delivery of scalable digital products, AI platforms, workflow automation
        systems, and enterprise modernisation initiatives — including consulting
        experience at{" "}
        <BoldLinks target="_blank" href="https://www.palo-it.com/en/">
          PALO IT Australia
        </BoldLinks>
        .
      </Description>
      <Description>
        I work closely with stakeholders, designers, product teams, and
        engineers to translate complex business requirements into high-quality
        technical solutions. As a Professional Scrum Developer (PSD I), I
        champion Agile practices, engineering excellence, and AI-assisted
        development methodologies that improve team productivity, software
        quality, and business outcomes.
      </Description>
      <Title>Experience</Title>
      <SubTitle>Palo IT — Senior Consultant</SubTitle>
      <SubDescription>Enterprise, AI &amp; agentic delivery</SubDescription>
      <TextIcon>
        <BoldSubTitle>Virgin Active</BoldSubTitle>
        <CustomIcon>
          <FitnessCenterIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <Description>
        Consulted and developed the new website and features for Virgin Active
        across Singapore, Thailand and Australia, including building the
        frontend for MyLocker, a new product and app for gym goers.
        Participated in design reviews, giving input to uplift the experience
        within the webpages.
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React" />
        <CustomButton text="Typescript" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>Vista — Agentic Orchestrator</BoldSubTitle>
        <CustomIcon>
          <HubIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <CustomDotpoint>
        Consulted Vista in the extraction of complex business logic using
        agentic workflows, rules and skills.
      </CustomDotpoint>
      <CustomDotpoint>
        Developed an agentic orchestrator capable of running complex workflows
        with different models.
      </CustomDotpoint>
      <CustomDotpoint>
        Expanded the orchestrator with integrations for GitHub, Azure, Cursor,
        Copilot and OpenRouter to pick up tickets and complete them autonomously
        with agentic methodology at a high accuracy and quality rate.
      </CustomDotpoint>
      <CustomDotpoint>
        Consulted Vista in the creation of implementation plans, as well as
        actioning them through the orchestrator to move code ranging from
        1980–2026 from an ancient stack (WinForms) to a modern stack
        (Angular/C#).
      </CustomDotpoint>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="Agentic Workflows" />
        <CustomButton text="OpenRouter" />
        <CustomButton text="Angular / C#" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>I-MED — IMOGEN Chatbot</BoldSubTitle>
        <CustomIcon>
          <SmartToyIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <Description>
        Created the middleware / chatbot for IMOGEN that handles messaging with
        an AI chatbot and escalates where necessary to Genesys for a live human
        agent. Consulted the I-MED technical team to solution and architect the
        app, and actioned the entire flow from pre-delivery through development,
        handover and go-live.
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="AI Chatbot" />
        <CustomButton text="Genesys" />
        <CustomButton text="Node / Typescript" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>Australian Payments Plus</BoldSubTitle>
        <CustomIcon>
          <PaymentsIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <Description>Entered the project midway, working on the following:</Description>
      <CustomDotpoint>Frontend (detail unavailable due to NDA).</CustomDotpoint>
      <CustomDotpoint>
        Consulting with developers as well as holding seminars on how to use AI
        (Gen-e2) to code, plan and design with code quality and a high success
        rate.
      </CustomDotpoint>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React/Typescript frontend" />
        <CustomButton text="Nodejs/Typescript backend" />
        <CustomButton text="Gen-e2" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>UrbnSurf — Phase 1 + Phase 1.5</BoldSubTitle>
        <CustomIcon>
          <PoolIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <Description>
        Entered the project shortly after discovery, with the following
        features:
      </Description>
      <CustomDotpoint>
        Full booking flow for guest and logged-in users with multiple flows.
      </CustomDotpoint>
      <CustomDotpoint>Authentication system.</CustomDotpoint>
      <CustomDotpoint>Dashboard.</CustomDotpoint>
      <CustomDotpoint>Memberships, multipacks and gift cards.</CustomDotpoint>
      <CustomDotpoint>
        Various other features including quick rebook.
      </CustomDotpoint>
      <CustomDotpoint>
        Complete CMS system to build custom pages as well as changing data
        within the website.
      </CustomDotpoint>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React/NextJs/Typescript frontend" />
        <CustomButton text="Nodejs/Typescript backend" />
        <CustomButton text="Figma Make" />
      </StackButtonWrapper>
      <SubTitle>4Mation Technologies — Fullstack Engineer</SubTitle>
      <SubDescription>AI, automation &amp; tech lead</SubDescription>
      <TextIcon>
        <BoldSubTitle>Industrial Deafness Australia</BoldSubTitle>
        <CustomIcon>
          <HearingIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <Description>
        Built a rostering calendar (custom react-big-calendar), a workflow
        system with react-flow, a custom form builder and a task management
        solution.
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React / Typescript" />
        <CustomButton text="Node / Strapi" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>QV / EGO AI Chatbot — Tech Lead</BoldSubTitle>
        <CustomIcon>
          <SmartToyIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <CustomDotpoint>
        Architected and developed a solution using multiple Amazon Bedrock
        Claude agents and lambdas to recommend products with accurate links,
        collect complaint information and email CS, and flag inappropriate
        conversations.
      </CustomDotpoint>
      <CustomDotpoint>
        Used multi-agent collaboration to increase accuracy and decrease
        latency, set up vector-friendly knowledge bases, and built a deep-chat
        frontend plus a Retool low-code dashboard to review conversations.
      </CustomDotpoint>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React / Typescript" />
        <CustomButton text="AWS Bedrock" />
        <CustomButton text="AWS Lambda" />
        <CustomButton text="Node / Typescript" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>Montgomery Homes — AI Email Categorizer</BoldSubTitle>
        <CustomIcon>
          <EmailIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <Description>
        Built a solution to categorize emails using OpenAI on a cronjob, with a
        rule engine to forward, label and move emails, and a Lovable frontend
        connected to a Strapi backend (with SSO) to review job logs and action
        unprocessed emails.
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React / Typescript" />
        <CustomButton text="OpenAI" />
        <CustomButton text="Strapi" />
        <CustomButton text="Typescript Cronjob" />
      </StackButtonWrapper>
      <SubTitle>Palo IT — Typescript Fullstack Engineer</SubTitle>
      <SubDescription>3 years 5 months - 2020-2023</SubDescription>
      <TextIcon>
        <BoldSubTitle>BookingSG V1.0 & RBS V2.0 </BoldSubTitle>
        <CustomIcon>
          <CalendarMonthIcon sx={{ paddingTop: "5px" }} />
        </CustomIcon>
      </TextIcon>
      <Description>
        V1.0 - created a complete booking system for admins, agency-admins and
        users to book services/agents within a calendar​
      </Description>
      <Description>
        V2.0 - created a Resource booking system for admins, agency-admins and
        users to book, including features such as Announcements, Notifications,
        Locations, User Groups etc. Final product{" "}
        <BoldLinks target="_blank" href="https://home.booking.gov.sg/">
          here
        </BoldLinks>
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React / Typescript frontend" />
        <CustomButton text="Nodejs / Typescript backend" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>First Rescue - IAG</BoldSubTitle>
        <CustomIcon>
          <SupportIcon />
        </CustomIcon>
      </TextIcon>
      <SelfBoldLink onClick={onClickFirstRescue}>
        To view more, click here!
      </SelfBoldLink>
      <CustomDotpoint>
        Created a whitelabel web-app to link customers to towing services which
        encompasses the entire flow from raising a assistance request to
        completing a service.
      </CustomDotpoint>
      <CustomDotpoint>
        Integrated{" "}
        <BoldLinks target="_blank" href="https://developers.google.com/maps">
          Google Maps
        </BoldLinks>{" "}
        and{" "}
        <BoldLinks target="_blank" href="https://www.servicenow.com/au/">
          Service Now
        </BoldLinks>{" "}
        to create constant tracking between roadside assistance and customer
      </CustomDotpoint>
      <CustomDotpoint>
        Integrated animations within the flow to enhance the consumer
        experience​
      </CustomDotpoint>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="Nextjs" />
        <CustomButton text="React" />
        <CustomButton text="Material Ui" />
        <CustomButton text="Google Maps" />
        <CustomButton text="Service Now" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>HSBC</BoldSubTitle>
        <CustomIcon>
          <AccountBalanceIcon />
        </CustomIcon>
      </TextIcon>
      <Description>
        Worked with HSBC team to implement a new solution for HSBC retail
        DCO/ICO website frontend to uplift the customer journey through
        application processes.
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React" />
        <CustomButton text="Nodejs" />
        <CustomButton text="Jenkins" />
        <CustomButton text="SonarQube​" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>Blocktrust</BoldSubTitle>
        <CustomIcon>
          <CurrencyBitcoinIcon />
        </CustomIcon>
      </TextIcon>
      <Description>
        Completed work to completely refactor and implement a seamless and
        efficient way of coding for a white-label blockchain/nft website that
        could be applied to multiple tenants.​
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="Nextjs Frontend" />
        <CustomButton text="C# Backend" />
      </StackButtonWrapper>
      <TextIcon>
        <BoldSubTitle>WhatTruck/Botany Access</BoldSubTitle>
        <CustomIcon>
          <LocalShippingIcon />
        </CustomIcon>
      </TextIcon>
      <Description>
        Built end to end marketplace app and web app for truck drivers to pick
        up heavy loads/machinery and posters to list their machinery to be
        delivered. You can see the final product{" "}
        <BoldLinks target="_blank" href="https://www.whattruck.com.au/">
          here
        </BoldLinks>
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React Native / Typescript Frontend" />
        <CustomButton text="NodeJs / Typscript Backend" />
      </StackButtonWrapper>
      <SubTitle>Zip Co Limited — Fullstack Engineer</SubTitle>
      <SubDescription>2018-2020, 2024-present</SubDescription>
      <Description>
        Set up and created components for{" "}
        <BoldLinks target="_blank" href="https://www.contentful.com/">
          Contentful
        </BoldLinks>{" "}
        CMS to display dynamic marketing pages — landing pages, loading scripts
        and extensions for events including Black Friday, Christmas and Zip Pay
        — and built scripts to speed up designer uploads, saving hours per week.
      </Description>
      <Description>
        Picked up .NET Core to maintain microservices and BAU, working with
        MySQL and{" "}
        <BoldLinks target="_blank" href="https://www.dynatrace.com">
          Dynatrace
        </BoldLinks>
        .
      </Description>
      <SubDescription>Stack</SubDescription>
      <StackButtonWrapper>
        <CustomButton text="React" />
        <CustomButton text=".NET Core / C#" />
        <CustomButton text="Contentful" />
        <CustomButton text="MySQL" />
        <CustomButton text="AWS Lambda" />
      </StackButtonWrapper>
    </>
  );
};

export default ExperienceDetails;
